import type { components } from '@primum/api-types';
import { fetchClient } from '@/util/fetchClient';

/**
 * Service wrapper for `POST /v2/inquiries/format-references-batch` — the
 * deterministic reference formatter used by the Expert Reference Flow
 * (ENG-492). PubMed/PMC URLs are enriched from NCBI; non-PubMed URLs come back
 * with safe placeholder strings so nothing is hallucinated.
 */

export type BatchReference = components['schemas']['BatchReference'];

export const FORMAT_REFERENCES_BATCH_DEFAULT_ERROR =
  'Unable to format these references. Please check the URLs and try again.';

const FORMAT_REFERENCES_BATCH_TIMEOUT_MS = 30_000;

export class FormatReferencesBatchError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number
  ) {
    super(message);

    this.name = 'FormatReferencesBatchError';
  }
}

/**
 * Call the batch formatter and unwrap its `{ success, data: { references } }`
 * envelope. Throws `FormatReferencesBatchError` on non-2xx or malformed
 * responses so callers can surface a user-facing error message.
 */
export async function formatReferencesBatch(urls: string[]): Promise<BatchReference[]> {
  const controller = new AbortController();

  const timeoutId = setTimeout(() => controller.abort(), FORMAT_REFERENCES_BATCH_TIMEOUT_MS);

  try {
    const { data, error, response } = await fetchClient.POST(
      '/v2/inquiries/format-references-batch',
      {
        body: { urls },
        signal: controller.signal,
      }
    );

    if (error || !data || data.success !== true) {
      const statusCode = response?.status ?? 500;

      const serverMessage =
        (error as { error?: string } | undefined)?.error ?? FORMAT_REFERENCES_BATCH_DEFAULT_ERROR;

      throw new FormatReferencesBatchError(serverMessage, statusCode);
    }

    return data.data.references;
  } catch (err) {
    if (err instanceof FormatReferencesBatchError) {
      throw err;
    }

    if (err instanceof Error && err.name === 'AbortError') {
      throw new FormatReferencesBatchError(
        'Formatting references timed out. Please try again.',
        408
      );
    }

    throw new FormatReferencesBatchError(FORMAT_REFERENCES_BATCH_DEFAULT_ERROR, 500);
  } finally {
    clearTimeout(timeoutId);
  }
}
